/**
 * Address search box above the address fields (plan 7, "Address search").
 * Picking a suggestion fills both address fields and places the pin; the
 * owner can still edit the text and move the pin, or skip this box entirely.
 */
import React, { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search } from 'lucide-react';
import { DEFAULT_AREA_ID, getArea } from '../../../config/areas';
import { useAddressSearch } from '../../../hooks/useAddressSearch';
import { AddressHit, AddressLang, addressesFromHit, formatAddress } from '../../../utils/addressSearch';
import type { LatLng } from '../../../utils/geo';
import { LISTING_LIMITS } from '../../../utils/listingEdit';
import { INPUT_CLASS } from './formFields';

export interface PickedAddress extends LatLng {
  addressHe: string;
  addressEn: string;
}

const HEBREW_LETTERS = /[֐-׿]/;

type Notice = 'filled' | 'filledNoEnglish' | null;

export default function AddressSearch({ areaId, onPick }: { areaId?: string; onPick: (picked: PickedAddress) => void }) {
  const { t } = useTranslation();
  const id = useId();
  const listId = `${id}-list`;
  const area = getArea(areaId ?? DEFAULT_AREA_ID) ?? getArea(DEFAULT_AREA_ID)!;
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [notice, setNotice] = useState<Notice>(null);
  // What the owner types decides the suggestions' language.
  const lang: AddressLang = HEBREW_LETTERS.test(query) ? 'he' : 'en';
  const { hits, status, findInOtherLanguage } = useAddressSearch(query, lang, area.center);
  const showList = open && status === 'done' && hits.length > 0;

  const pick = async (hit: AddressHit) => {
    setOpen(false);
    setActive(-1);
    const other = await findInOtherLanguage(hit);
    const texts = addressesFromHit(hit, lang, other, area, LISTING_LIMITS.addressLength);
    onPick({ ...texts, lat: hit.lat, lng: hit.lng });
    setQuery('');
    setNotice(texts.addressEn && texts.addressHe ? 'filled' : 'filledNoEnglish');
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showList) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => (i + 1) % hits.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => (i <= 0 ? hits.length - 1 : i - 1));
    } else if (e.key === 'Enter' && active >= 0) {
      e.preventDefault();
      pick(hits[active]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div>
      <label htmlFor={id} className="flex items-center gap-1 text-sm font-medium text-ink mb-1">
        {t('directory.admin.location.search.label')}
      </label>
      <p id={`${id}-hint`} className="mb-2 text-xs text-ash">
        {t('directory.admin.location.search.hint')}
      </p>
      <div className="relative">
        <Search className="pointer-events-none absolute top-1/2 -translate-y-1/2 start-3 w-4 h-4 text-ash" aria-hidden />
        <input
          id={id}
          type="text"
          dir={query ? (lang === 'he' ? 'rtl' : 'ltr') : undefined}
          role="combobox"
          aria-expanded={showList}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={showList && active >= 0 ? `${listId}-${active}` : undefined}
          aria-describedby={`${id}-hint ${id}-status`}
          autoComplete="off"
          value={query}
          placeholder={t('directory.admin.location.search.placeholder')}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            setActive(-1);
            setNotice(null);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => setOpen(false)}
          onKeyDown={onKeyDown}
          className={`${INPUT_CLASS} ps-9`}
        />
        {showList && (
          <ul
            id={listId}
            role="listbox"
            className="absolute z-[1000] mt-1 w-full overflow-hidden rounded-[10px] border border-ink bg-paper shadow-lg"
          >
            {hits.map((hit, i) => (
              <li
                key={hit.key}
                id={`${listId}-${i}`}
                role="option"
                aria-selected={i === active}
                dir={lang === 'he' ? 'rtl' : 'ltr'}
                // mousedown, so the pick happens before the box loses focus and closes the list.
                onMouseDown={(e) => {
                  e.preventDefault();
                  pick(hit);
                }}
                className={`min-h-[44px] px-3 py-2 cursor-pointer text-sm text-ink ${i === active ? 'bg-fog' : 'hover:bg-fog'}`}
              >
                <span className="block">{formatAddress(hit, undefined)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
      <p id={`${id}-status`} role="status" className="mt-1 text-xs text-ash">
        {status === 'searching' && t('directory.admin.location.search.searching')}
        {status === 'done' && hits.length === 0 && t('directory.admin.location.search.notFound')}
        {status === 'failed' && t('directory.admin.location.search.failed')}
        {status === 'idle' && notice && t(`directory.admin.location.search.${notice}`)}
      </p>
      <p className="mt-1 text-[11px] text-ash">
        {t('directory.admin.location.search.credit')}{' '}
        <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer" className="underline">
          © OpenStreetMap
        </a>
      </p>
    </div>
  );
}
